import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmbedLink } from '../../../../../../components';
import { UserContext } from '../../../../../../context';
import {
  Story,
  StoryCard,
  storyCardApi,
  StoryCardContentGallery,
  StoryCardStatus,
} from '../../../../../../services';
import { useAppDispatch } from '../../../../../hooks';
import { GalleryItem, ImageFile } from '../../../../../models';
import {
  getDeepLinkUrl,
  getStoryCardIntro,
  getStoryCardPreviewUrl,
  getStoryCardSource,
  getStoryCardTitle,
  stripHtmlString,
} from '../../../../../utils';
import { AutoComplete } from '../../../../AutoComplete';
import { DeeplinkType } from '../../../../Deeplink';
import { IconLabel } from '../../../../IconLabel';
import { StoryNodeSelect } from '../../StoryNode';
import { CloseWrapper, StoryCardMediaPlaceholder, useBlockToolInputFocus } from '../../helpers';

import classes from './StoryCardNodeView.module.scss';

interface ResetLinksArgs {
  resetStory: boolean;
  resetStoryCard?: boolean;
}

export const StoryCardNodeView: FunctionComponent<NodeViewProps> = ({
  editor,
  node,
  getPos,
  updateAttributes,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { onBlockInputFocus } = useBlockToolInputFocus({ editor, getPos });

  const { storyCard } = node.attrs;

  const [story, setStory] = useState<Story | null>(storyCard?.story ?? null);

  const { channelId } = useContext(UserContext).userInfo.userData;

  const itemTemplate = useCallback(
    (storyCard: StoryCard) => {
      const title = getStoryCardTitle(storyCard);

      const text = getStoryCardIntro(storyCard);

      const { type, content } = storyCard;

      const { gallery } = content as StoryCardContentGallery;

      const galleryItem = gallery ? (gallery[0] as GalleryItem) : undefined;

      const previewUrl = getStoryCardPreviewUrl({ type, galleryItem });

      return (
        <div className={classes['card']}>
          <div className={classes['card__fields']}>
            <div className={classes['card__fields-header']}>
              <div className={classes['card__fields-header-type']}>{t(type.toLowerCase())}</div>
              {title && (
                <div className={classes['card__fields-header-title']}>&nbsp;•&nbsp;{title}</div>
              )}
            </div>
            {text && <div className={classes['card__fields-text']}>{stripHtmlString(text)}</div>}
          </div>
          {previewUrl && (
            <div
              className={classes['card__teaser']}
              style={{ backgroundImage: `url(${previewUrl})` }}
            ></div>
          )}
        </div>
      );
    },
    [t]
  );

  const resetLinks = useCallback(
    ({ resetStory, resetStoryCard }: ResetLinksArgs) => {
      if (!resetStory && !resetStory) {
        return null;
      }

      return (
        <div className={classes['reset-links']}>
          {resetStory && (
            <IconLabel
              label={t('richTextEditor.change-story-label')}
              onClick={() => {
                setStory(null);
                updateAttributes({ storyCard: null });
              }}
              singleColor
            />
          )}
          {resetStoryCard && (
            <IconLabel
              label={t('richTextEditor.change-card-label')}
              onClick={() => updateAttributes({ storyCard: null })}
              singleColor
            />
          )}
        </div>
      );
    },
    [t, updateAttributes]
  );

  const content = useMemo(() => {
    if (!story) {
      return <StoryNodeSelect story={story} setStory={setStory} onFocus={onBlockInputFocus} />;
    }

    if (storyCard) {
      const { storyId, id: cardId, type, content } = storyCard;

      const sourceName = getStoryCardSource(storyCard);

      const title = getStoryCardTitle(storyCard);

      const text = getStoryCardIntro(storyCard);

      const { gallery } = content as StoryCardContentGallery;

      const galleryItem = gallery ? (gallery[0] as GalleryItem) : undefined;

      const imageUrl = getStoryCardPreviewUrl({ type, galleryItem });

      const url = getDeepLinkUrl({ type: DeeplinkType.CARD, channelId, storyId, cardId });

      if ([sourceName, title, text, imageUrl].every((value) => !Boolean(value))) {
        return <StoryCardMediaPlaceholder url={url} type={type} />;
      }

      return (
        <>
          <EmbedLink
            link={{ url, sourceName, title, text, image: { url: imageUrl } as ImageFile }}
            hideUrl
          />

          {resetLinks({ resetStory: true, resetStoryCard: true })}
        </>
      );
    }

    const fetchQuery = (query: string, page?: number) => {
      return dispatch(
        storyCardApi.endpoints.storyCardsFeed.initiate({
          page,
          storyId: story.id,
          filter: { query, onlyCompletedMedia: true, status: [StoryCardStatus.PUBLISHED] },
        })
      );
    };

    return (
      <>
        <AutoComplete
          propToIdentify={'id'}
          propToDisplay={'id'}
          fetchQuery={fetchQuery}
          itemTemplate={itemTemplate}
          value={[storyCard].filter(Boolean)}
          onChange={(cards: StoryCard[]) => updateAttributes({ storyCard: cards.slice(-1).pop() })}
          onFocus={onBlockInputFocus}
          placeholder={t('richTextEditor.search-card-placeholder')}
        />

        {resetLinks({ resetStory: true })}
      </>
    );
  }, [
    channelId,
    dispatch,
    itemTemplate,
    onBlockInputFocus,
    resetLinks,
    story,
    storyCard,
    t,
    updateAttributes,
  ]);

  return (
    <NodeViewWrapper as={'div'}>
      <CloseWrapper editor={editor} node={node} getPos={getPos}>
        {content}
      </CloseWrapper>
    </NodeViewWrapper>
  );
};
